import { Injectable } from '@angular/core';
import { ApiQuery } from '@kinet-open-network/client/core/api-client';
import {
  FilterMatchMode,
  FilterMetadata,
  FilterOperator,
  LazyLoadMeta,
  SortMeta,
} from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { Compare, Filter, LogExp, parse, stringify } from 'scim2-parse-filter';
import { FilterType } from './resource-table.component';

export type TableQuery = TableLazyLoadEvent;
export type TableApiQuery = ApiQuery;

const compareOperators = ['eq', 'ne', 'co', 'sw', 'ew', 'gt', 'lt', 'ge', 'le'];

const isCompare = (filter: Filter): filter is Compare => {
  return compareOperators.includes(filter.op);
};

const isLogExp = (filter: Filter): filter is LogExp => {
  return filter.op === 'and' || filter.op === 'or';
};

export interface ToTableQueryOptions {
  filterTypes: Record<string, FilterType | undefined>;
}

type MatchModeMap = Partial<Record<FilterType, [string, string]>> & {
  default: [string, string];
};

@Injectable()
export class ResourceTableQueryAdapter {
  toApiQuery(event: TableQuery): TableApiQuery {
    return (<TableApiQuery>{
      perPage: event.rows ?? undefined,
      sort: this.toApiSort(event.multiSortMeta),
      page:
        event.first !== undefined && event.rows
          ? Math.floor(event.first / event.rows) + 1
          : undefined,
      filters: this.toApiFilters(event.filters),
    }) satisfies TableApiQuery;
  }

  toTableQuery(
    query: TableApiQuery | null | undefined,
    options: ToTableQueryOptions,
  ): TableQuery {
    if (!query) {
      return {};
    }

    const tableQuery: TableQuery = {
      multiSortMeta: this.toTableSort(query.sort),
      first:
        query.page && query.perPage
          ? (query.page - 1) * query.perPage
          : undefined,
      rows: query.perPage,
      filters: this.toTableFilters(
        query.filters?.toString(),
        options.filterTypes,
      ),
    };

    return tableQuery;
  }

  private toTableSort(sorts?: string[]): SortMeta[] | undefined {
    if (!sorts) {
      return undefined;
    }

    if (typeof sorts === 'string') {
      sorts = [sorts];
    }

    return sorts.map((sort) => {
      if (sort.startsWith('-')) {
        return { field: sort.substring(1), order: -1 };
      }

      return { field: sort, order: 1 };
    });
  }

  private toApiSort(
    sorts: SortMeta[] | undefined | null,
  ): string[] | undefined {
    if (!sorts) {
      return undefined;
    }

    return sorts.map((sort) => {
      return sort.order === -1 ? `-${sort.field}` : sort.field;
    });
  }

  private toTableFilters(
    filters: string | undefined,
    types: ToTableQueryOptions['filterTypes'],
  ): LazyLoadMeta['filters'] {
    if (!filters) {
      return undefined;
    }
    const filter: Filter = parse(filters);
    const meta: LazyLoadMeta['filters'] = {};

    const getFilterType = (attr: string) => {
      return types[attr] ?? 'text';
    };

    const parseFilterValueByType = (attr: string, value: any) => {
      const type = getFilterType(attr);

      switch (type) {
        case 'text':
          return value?.toString();
        case 'date':
          return new Date(value);
        case 'numeric':
          return Number(value);
        case 'boolean':
          return Boolean(value);
        case 'select':
          return Array.isArray(value) ? value : [value];
        default:
          return value;
      }
    };

    const convertToMetaFilters = (
      filter: Filter,
      operator: 'and' | 'or' = 'and',
      negated = false,
    ) => {
      const appendFilter = (attr: string, value: any, matchMode: string) => {
        // override IN filter
        if (getFilterType(attr) === 'select') {
          if (matchMode?.startsWith('not')) {
            return;
            throw new Error('NOT IN filter is not supported by resource table');
          }

          let inFilterMeta: FilterMetadata;

          // check if filter already exists in metadata
          if (!meta[attr]) {
            inFilterMeta = {
              value: [],
              matchMode: FilterMatchMode.IN,
              operator,
            };
            meta[attr] = inFilterMeta;
          } else {
            // if it is array of filters -> reduce all filters to one IN filter
            if (Array.isArray(meta[attr])) {
              inFilterMeta = (meta[attr]! as FilterMetadata[]).reduce(
                (acc, meta) => {
                  acc['value'].push(
                    ...parseFilterValueByType(attr, meta.value),
                  );
                  return acc;
                },
                {
                  value: [],
                  matchMode: FilterMatchMode.IN,
                  operator,
                } as FilterMetadata,
              );
            } else {
              // otherwise it is already IN filter, but just to
              // be sure, we explicitly cast it to IN filter
              inFilterMeta = {
                value: parseFilterValueByType(
                  attr,
                  (meta[attr]! as FilterMetadata).value,
                ),
                matchMode: FilterMatchMode.IN,
                operator,
              };
            }
          }

          // push value to IN filter
          inFilterMeta.value.push(...parseFilterValueByType(attr, value));

          // push IN filter to meta
          meta[attr] = [inFilterMeta];
        } else {
          if (!meta[attr]) {
            meta[attr] = [];
          }
          const parsedVal =
            matchMode === 'present'
              ? Boolean(value)
              : parseFilterValueByType(attr, value);
          (<FilterMetadata[]>meta[attr]).push({
            value: parsedVal,
            matchMode,
            operator,
          });
        }
      };

      const negateMatchMode = (modes: [string, string]) => {
        return modes[negated ? 1 : 0];
      };

      const pickMatchMode = (
        attr: string,
        matchModes: MatchModeMap,
      ): string => {
        return negateMatchMode(
          matchModes[getFilterType(attr)] ?? matchModes.default,
        );
      };

      switch (filter.op) {
        case 'eq':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            pickMatchMode(filter.attrPath, {
              date: [FilterMatchMode.DATE_IS, FilterMatchMode.DATE_IS_NOT],
              default: [FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS],
            }),
          );
          break;
        case 'co':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            pickMatchMode(filter.attrPath, {
              boolean: [FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS],
              default: [FilterMatchMode.CONTAINS, FilterMatchMode.NOT_CONTAINS],
            }),
          );
          break;
        case 'sw':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            negateMatchMode([
              FilterMatchMode.STARTS_WITH,
              FilterMatchMode.NOT_CONTAINS,
            ]), // NOT_STARTS_WITH does not exist
          );
          break;
        case 'ew':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            negateMatchMode([
              FilterMatchMode.ENDS_WITH,
              FilterMatchMode.NOT_CONTAINS,
            ]), // NOT_ENDS_WITH does not exist
          );
          break;
        case 'gt':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            pickMatchMode(filter.attrPath, {
              date: [FilterMatchMode.DATE_AFTER, FilterMatchMode.DATE_BEFORE],
              default: [
                FilterMatchMode.GREATER_THAN,
                FilterMatchMode.LESS_THAN,
              ],
            }),
          );
          break;
        case 'lt':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            pickMatchMode(filter.attrPath, {
              date: [FilterMatchMode.DATE_BEFORE, FilterMatchMode.DATE_AFTER],
              default: [
                FilterMatchMode.LESS_THAN,
                FilterMatchMode.GREATER_THAN,
              ],
            }),
          );
          break;
        case 'ge':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            negateMatchMode([
              FilterMatchMode.GREATER_THAN_OR_EQUAL_TO,
              FilterMatchMode.LESS_THAN_OR_EQUAL_TO,
            ]),
          );
          break;
        case 'le':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            negateMatchMode([
              FilterMatchMode.LESS_THAN_OR_EQUAL_TO,
              FilterMatchMode.GREATER_THAN_OR_EQUAL_TO,
            ]),
          );
          break;
        case 'ne':
          appendFilter(
            filter.attrPath,
            filter.compValue,
            pickMatchMode(filter.attrPath, {
              date: [FilterMatchMode.DATE_IS_NOT, FilterMatchMode.DATE_IS],
              default: [FilterMatchMode.NOT_EQUALS, FilterMatchMode.EQUALS],
            }),
          );
          break;
        case 'or':
        case 'and':
          filter.filters.forEach((f) => convertToMetaFilters(f, filter.op));
          break;
        case 'not':
          convertToMetaFilters(filter.filter, operator, true);
          break;
        case 'pr':
          appendFilter(filter.attrPath, negated ? false : true, 'present');
          break;
        case '[]':
          throw new Error('[] operator is not supported!');
      }
    };

    convertToMetaFilters(filter);

    return meta;
  }

  private toApiFilters(filters: LazyLoadMeta['filters']): string | undefined {
    if (!filters) {
      return undefined;
    }

    const checkMetaHasValue = (meta: FilterMetadata) => {
      return meta.value != null || meta.value != undefined;
    };

    const filter = Object.entries(filters)
      .filter(([key, meta]) => key != 'global') // filter out global filter
      .map(([key, meta]) => {
        if (!meta) {
          return { key, meta: [] };
        }

        if (Array.isArray(meta)) {
          return { key, meta: meta.filter(checkMetaHasValue) };
        }

        return {
          key,
          meta: checkMetaHasValue(meta) ? [meta] : [],
        };
      })
      .filter(({ meta }) => meta.length > 0)
      .reduce<LogExp>(
        (filter, { key, meta }) => {
          filter.filters.push(...this.filterMetadataToApiFilters(key, meta));

          return filter;
        },
        <LogExp>{
          op: 'and',
          filters: [],
        },
      );

    return filter.filters.length > 0 ? stringify(filter) : undefined;
  }

  private filterMetadataToApiFilters(
    key: string,
    meta: FilterMetadata[],
  ): Filter[] {
    if (meta.length == 0) {
      return [];
    }

    const filters = meta.reduce<Filter[]>((filters, meta) => {
      const { value } = meta;

      switch (meta.matchMode) {
        case FilterMatchMode.EQUALS:
        case FilterMatchMode.DATE_IS:
        case FilterMatchMode.IS:
          filters.push({
            op: 'eq',
            attrPath: key,
            compValue: value,
          });
          break;
        case FilterMatchMode.CONTAINS:
          filters.push({
            op: 'co',
            attrPath: key,
            compValue: value,
          });
          break;
        // TODO: zistit co je after/date_after, neurobit radsej greater or equal??
        case FilterMatchMode.AFTER:
        case FilterMatchMode.DATE_AFTER:
        case FilterMatchMode.GREATER_THAN:
          filters.push({
            op: 'gt',
            attrPath: key,
            compValue: value,
          });
          break;
        // TODO: zistit co je before/date before neurobit radsej lte??
        case FilterMatchMode.BEFORE:
        case FilterMatchMode.DATE_BEFORE:
        case FilterMatchMode.LESS_THAN:
          filters.push({
            op: 'lt',
            attrPath: key,
            compValue: value,
          });
          break;
        case FilterMatchMode.DATE_IS_NOT:
        case FilterMatchMode.NOT_EQUALS:
          filters.push({
            op: 'not',
            filter: {
              op: 'eq',
              attrPath: key,
              compValue: value,
            },
          });
          break;
        case FilterMatchMode.NOT_CONTAINS:
          filters.push({
            op: 'not',
            filter: {
              op: 'co',
              attrPath: key,
              compValue: value,
            },
          });
          break;
        case FilterMatchMode.GREATER_THAN_OR_EQUAL_TO:
          filters.push({
            op: 'ge',
            attrPath: key,
            compValue: value,
          });
          break;
        case FilterMatchMode.LESS_THAN_OR_EQUAL_TO:
          filters.push({
            op: 'le',
            attrPath: key,
            compValue: value,
          });
          break;
        case FilterMatchMode.STARTS_WITH:
          filters.push({
            op: 'sw',
            attrPath: key,
            compValue: value,
          });
          break;
        case FilterMatchMode.ENDS_WITH:
          filters.push({
            op: 'ew',
            attrPath: key,
            compValue: value,
          });
          break;
        case FilterMatchMode.IN:
          // eslint-disable-next-line no-case-declarations
          const inFilters = Array.isArray(value)
            ? value.map(
                (inValue) =>
                  <Filter>{ op: 'eq', attrPath: key, compValue: inValue },
              )
            : [];

          if (inFilters.length > 0) {
            filters.push({
              op: 'or',
              filters: inFilters,
            });
          }
          break;
        case 'present':
          if (value && value !== 'false') {
            filters.push({
              op: 'pr',
              attrPath: key,
            });
          } else {
            filters.push({
              op: 'not',
              filter: {
                op: 'pr',
                attrPath: key,
              },
            });
          }
          break;
        default:
          throw new Error(
            "Filter match mode '" + meta.matchMode + "' is not supported!",
          );
      }
      return filters;
    }, []);

    if (meta.length == 1) {
      return filters;
    }

    return [
      {
        op: meta[0].operator === FilterOperator.OR ? 'or' : 'and',
        filters,
      },
    ];
  }

  // private apiFilters
}
